import {
  AppBar,
  Button,
  Dialog,
  Grid,
  IconButton,
  Slide,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import { TransitionProps } from '@material-ui/core/transitions';
import { Close as CloseIcon } from '@material-ui/icons';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IOpportunityApprovalRequestReport } from '../../../../models/report.model';
import { downloadOpportunityApprovalRequestReportList } from '../../../../services/reportService';
import { PageFilter } from '../../../../state/app';
import { AppState } from '../../../../state/configureStore';
import {
  clearReport,
  fetchOpportunityApprovalRequestReportList,
  IOpportunityApprovalRequestReportFilter,
  ReportState,
} from '../../../../state/report';
import {
  DateTimeDisplay,
  PageContainer,
  PageTitle,
  TableLoading,
} from '../../../components';
import { useFilter, useTableSorting } from '../../../hooks';
import OpportunityApprovalRequestReportFilter from './OpportunityApprovalRequestReportFilter';
import useStyles from './OpportunityApprovalRequestReportStyle';

interface IOpportunityApprovalRequestReportDialog {
  open: boolean;
  handleClose: () => void;
}

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children?: React.ReactElement },
  ref: React.Ref<unknown>,
) {
  return (
    <Slide direction="up" ref={ref} {...props} timeout={{ enter: 400, exit: 400 }} />
  );
});

const OpportunityApprovalRequestReportDialog: React.FC<
  IOpportunityApprovalRequestReportDialog
> = (props) => {
  const classes = useStyles();
  const { open, handleClose } = props;
  const dispatch = useDispatch();
  const initFilter: IOpportunityApprovalRequestReportFilter = {};
  const [filters, setFilters] = useFilter<IOpportunityApprovalRequestReportFilter>(
    initFilter,
    true,
  );
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [paging, setPaging] = useState<PageFilter>({
    pageNumber: 0,
    pageSize: 15,
  });

  const {
    loading,
    items: opportunityApprovalRequestReportList,
    totalItems,
  } = useSelector<AppState, ReportState>((state) => state.report);

  useEffect(() => {
    if (open) {
      dispatch(fetchOpportunityApprovalRequestReportList(filters, { ...paging }));
    }
    return function cleanUp() {
      dispatch(clearReport());
    };
  }, [dispatch, paging, filters, open]);

  const handleChangeFilter = (
    newFilter: IOpportunityApprovalRequestReportFilter,
  ): void => {
    setFilters(newFilter);
    setPaging({
      ...paging,
      pageNumber: 0,
    });
  };

  const handleChangePageNumber = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number,
  ) => {
    setPaging({ ...paging, pageNumber: newPage });
  };

  const handleChangePageSize = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setPaging({
      ...paging,
      pageSize: +event.target.value,
      pageNumber: 0,
    });
  };

  const exportExcel = () => {
    downloadOpportunityApprovalRequestReportList(filters, {
      pageSize: totalItems,
    }).catch((err) => console.error(err));
  };

  return (
    <Dialog fullScreen open={open} onClose={handleClose} TransitionComponent={Transition}>
      <AppBar className={classes.appBar}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
          <Typography variant="h6" className={classes.title}>
            Assignment Approval Request Report
          </Typography>
        </Toolbar>
      </AppBar>
      <PageContainer>
        <PageTitle>Assignment Approval Request Report</PageTitle>
        <OpportunityApprovalRequestReportFilter
          filters={filters}
          onChangeFilter={handleChangeFilter}
          initialFilter={initFilter}
        />
        {!isSmallScreen && (
          <div className={classes.filterContainer}>
            <Button
              variant={'contained'}
              className={classes.button}
              onClick={exportExcel}
            >
              Export Excel
            </Button>
          </div>
        )}
        {open && (
          <Grid container spacing={3} className={classes.container}>
            <Grid item xs={12}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>No.</TableCell>
                    <TableCell>Company</TableCell>
                    <TableCell>Assignment Number</TableCell>
                    <TableCell>Project</TableCell>
                    <TableCell>Project Owner</TableCell>
                    <TableCell>Program</TableCell>
                    <TableCell>Brand</TableCell>
                    <TableCell>Requested By</TableCell>
                    <TableCell>Requested Date</TableCell>
                    <TableCell>Responded</TableCell>
                    <TableCell>Responded Date</TableCell>
                    <TableCell>Comment</TableCell>
                    <TableCell>Response</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableLoading isLoading={loading} colSpan={13}></TableLoading>
                  {opportunityApprovalRequestReportList.map(
                    (item: IOpportunityApprovalRequestReport, index) => {
                      return (
                        <TableRow key={index}>
                          <TableCell>
                            {index + paging.pageSize * paging.pageNumber + 1}
                          </TableCell>
                          <TableCell>{item.companyName}</TableCell>
                          <TableCell>{item.assignmentNumber}</TableCell>
                          <TableCell>{item.projectName}</TableCell>
                          <TableCell>{item.projectOwner}</TableCell>
                          <TableCell>{item.programName}</TableCell>
                          <TableCell>{item.brandName}</TableCell>
                          <TableCell>{item.requestedBy}</TableCell>
                          <TableCell>
                            <DateTimeDisplay value={item.requestedDate} />
                          </TableCell>
                          <TableCell>{item.respondedBy}</TableCell>
                          <TableCell>
                            <DateTimeDisplay value={item.respondedDate} />
                          </TableCell>
                          <TableCell>{item.comment}</TableCell>
                          <TableCell>{item.responseResult}</TableCell>
                        </TableRow>
                      );
                    },
                  )}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[15, 25, 35, 500]}
                component="div"
                count={totalItems}
                rowsPerPage={paging.pageSize}
                page={paging.pageNumber}
                onPageChange={handleChangePageNumber}
                onRowsPerPageChange={handleChangePageSize}
              />
            </Grid>
          </Grid>
        )}
      </PageContainer>
    </Dialog>
  );
};

export default OpportunityApprovalRequestReportDialog;
